import { useMemo, useState } from "react";
import { Building2, Clock, Mail, MapPin, Pencil, Phone, User } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { Modal } from "@/components/ui/customs/modal";
import { formatOpeningHoursDisplay } from "@/lib/dates.util";
import { GetAllCompanyDto, GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { OpeningHours } from "@/types/forms/auth/registerFormData.form";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";

type BillingEntity = GetAllCompanyDto["billingEntities"][number];
type PickupLocation = GetAllCompanyDto["pickUpLocations"][number];

const AdditionalInfoCard = ({
  expedition,
  billingEntity,
  pickupLocation,
  onEditBilling,
  onEditPickup,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  billingEntity?: BillingEntity;
  pickupLocation?: PickupLocation;
  onEditBilling?: () => void;
  onEditPickup?: () => void;
}) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const user = expedition.createdBy;

  const billingAddress =
    billingEntity?.street + ", " + billingEntity?.postalCode + " " + billingEntity?.city;
  const pickupAddress = pickupLocation
    ? pickupLocation?.street + ", " + pickupLocation?.postalCode + " " + pickupLocation?.city
    : expedition.pickupInfo?.pickupStreet +
      ", " +
      expedition.pickupInfo?.pickupPostalCode +
      " " +
      expedition.pickupInfo?.pickupCity;

  const pickupOpeningSummary = useMemo(() => {
    if (!pickupLocation) return "";
    return formatOpeningHoursDisplay(pickupLocation.openingHours as OpeningHours);
  }, [pickupLocation]);

  const userInfo = user ? user.firstName + " " + user.lastName : "-";

  return (
    <div className="flex w-full flex-col gap-2 rounded-md bg-slate-100 p-3">
      <p className="text-sm text-gray-500">{t("sidePanelAdditionalInfo.userLabel")}</p>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-1">
          <User className="h-4 w-4 text-gray-500" />
          <p className="text-sm text-gray-900">{userInfo}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <Phone className="h-4 w-4 text-gray-500" />
          <p className="text-sm text-gray-900">{user?.mobilePhoneNumber || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <Mail className="h-4 w-4 text-gray-500" />
          <p className="text-sm text-gray-900">{user?.email || "-"}</p>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <p className="pt-2! text-sm text-gray-500">{t("sidePanelAdditionalInfo.billingLabel")}</p>
        {onEditBilling && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onEditBilling}
            aria-label={t("sidePanelAdditionalInfo.editBillingEntity")}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-start justify-start gap-1">
          <Building2 className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <p className="text-sm text-gray-900">{billingEntity?.companyName || "-"}</p>
        </div>
        <div className="flex flex-row items-start justify-start gap-1">
          <MapPin className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <p className="text-sm text-gray-900">{billingAddress || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <Mail className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <p className="text-sm text-gray-900">{billingEntity?.email || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <p className="text-sm text-gray-500">EORI</p>
          <p className="text-sm text-gray-900">{billingEntity?.companyEori || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <p className="text-sm text-gray-500">MID</p>
          <p className="text-sm text-gray-900">{billingEntity?.midCode || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <p className="text-sm text-gray-500">{t("sidePanelAdditionalInfo.vatLabel")}</p>
          <p className="text-sm text-gray-900">{billingEntity?.numberTVA || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <p className="text-sm text-gray-500">SIRET</p>
          <p className="text-sm text-gray-900">{billingEntity?.companySiret || "-"}</p>
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <p className="pt-2! text-sm text-gray-500">{t("sidePanelAdditionalInfo.pickupLabel")}</p>
        {onEditPickup && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onEditPickup}
            aria-label={t("sidePanelAdditionalInfo.editPickupLocation")}
            disabled={!pickupLocation}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-start justify-start gap-1">
          <Building2 className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <p className="text-sm text-gray-900">
            {pickupLocation?.name || expedition.pickupInfo?.pickupCompany || "-"}
          </p>
        </div>
        <div className="flex flex-row items-start justify-start gap-1">
          <MapPin className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <p className="text-sm text-gray-900">{pickupAddress || "-"}</p>
        </div>
        <div className="flex flex-row items-center gap-1">
          <Phone className="h-4 w-4 text-gray-500" />
          <p className="text-sm text-gray-900">
            {pickupLocation?.contactNumber || expedition.pickupInfo?.pickupPhone || "-"}
          </p>
        </div>
        <div className="flex flex-row items-start justify-start gap-1">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <p className="text-sm text-gray-900">{pickupOpeningSummary || "-"}</p>
        </div>
      </div>
    </div>
  );
};

export const SideContentAdditionalInfo = ({
  expedition,
  company,
  readOnly = false,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  company: GetAllCompanyDto;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const [billingPickupModal, setBillingPickupModal] = useState<"billing" | "pickup" | null>(null);
  const { billingEntities, pickUpLocations } = company;
  const [billingEntityWatch, pickupLocationWatch] = useWatch({
    control: methods.control,
    name: ["billingEntityId", "pickUpLocationId"],
  });
  const billingEntityId = readOnly
    ? expedition.expeditionBillingDetail?.billingEntityId
    : billingEntityWatch;
  const pickupLocationId = readOnly
    ? expedition.pickupInfo?.pickupLocation?.id
    : pickupLocationWatch;

  const billingEntity = billingEntities.find(
    (billingEntity) => billingEntity.id === billingEntityId,
  );
  const pickupLocation = pickUpLocations.find(
    (pickupLocation) => pickupLocation.id === pickupLocationId,
  );

  if (readOnly) {
    return (
      <AdditionalInfoCard
        expedition={expedition}
        billingEntity={billingEntity}
        pickupLocation={pickupLocation}
      />
    );
  }

  return (
    <>
      <AdditionalInfoCard
        expedition={expedition}
        billingEntity={billingEntity}
        pickupLocation={pickupLocation}
        onEditBilling={() => setBillingPickupModal("billing")}
        onEditPickup={() => setBillingPickupModal("pickup")}
      />
      <Modal
        open={billingPickupModal !== null}
        onClose={() => setBillingPickupModal(null)}
        title={
          billingPickupModal === "billing"
            ? t("sidePanelAdditionalInfo.billingEntityModalTitle")
            : t("sidePanelAdditionalInfo.pickupLocationModalTitle")
        }
        contentClassName="min-w-0 overflow-x-hidden"
        footer={
          <Button
            className="w-full"
            type="button"
            variant="outline"
            onClick={() => setBillingPickupModal(null)}
          >
            {t("common:actions.close")}
          </Button>
        }
      >
        <div className="w-full max-w-full min-w-0">
          {billingPickupModal === "billing" && (
            <ControlledSelectV2
              options={billingEntities.map((billingEntity) => ({
                value: billingEntity.id,
                label: `${billingEntity.companyName}`,
              }))}
              setValue={(e: string) => methods.setValue("billingEntityId", e)}
              label={t("sidePanelAdditionalInfo.billingEntityLabel")}
              required
              control={methods.control}
              name="billingEntityId"
              wrapperClassName="w-full min-w-0"
              contentClassName="min-w-0 max-w-full overflow-hidden"
              triggerClassName="min-w-0 max-w-full overflow-hidden"
            />
          )}
          {billingPickupModal === "pickup" && (
            <ControlledSelectV2
              control={methods.control}
              name="pickUpLocationId"
              options={pickUpLocations.map((pickUpLocation) => ({
                value: pickUpLocation.id,
                label: `${pickUpLocation.name} ${pickUpLocation.street} ${pickUpLocation.postalCode} ${pickUpLocation.city}`,
              }))}
              label={t("sidePanelAdditionalInfo.pickupLocationLabel")}
              required
              setValue={(e: string) => methods.setValue("pickUpLocationId", e)}
              wrapperClassName="w-full min-w-0"
              contentClassName="min-w-0 max-w-full overflow-hidden"
              triggerClassName="min-w-0 max-w-full overflow-hidden"
            />
          )}
        </div>
      </Modal>
    </>
  );
};
